import { clearAllBrowserData } from './storageUtils';

const VERSION_KEY = 'app_version';
const LAST_CLEAR_KEY = 'last_version_clear';

export const getCurrentVersion = () => {
  return process.env.REACT_APP_VERSION || 'unknown';
};

export const getStoredVersion = () => {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch (error) {
    console.warn('Could not access localStorage for version:', error);
    return null;
  }
};

export const setStoredVersion = (version) => {
  try {
    localStorage.setItem(VERSION_KEY, version);
    localStorage.setItem(LAST_CLEAR_KEY, new Date().toISOString());
  } catch (error) {
    console.warn('Could not store version in localStorage:', error);
  }
};

export const isNewVersion = () => {
  const currentVersion = getCurrentVersion();
  const storedVersion = getStoredVersion();
  
  // Skip version check for development environment
  if (currentVersion === 'dev' || currentVersion === 'unknown') {
    return false;
  }
  
  // If no stored version, this is first visit
  if (!storedVersion) {
    return false; // Don't clear on first visit
  }
  
  // Check if versions differ
  return currentVersion !== storedVersion;
};

export const checkVersionAndClear = async () => {
  try {
    const currentVersion = getCurrentVersion();
    const storedVersion = getStoredVersion();
    
    
    // Skip in development or unknown environments
    if (currentVersion === 'dev' || currentVersion === 'unknown') {
      return {
        cleared: false,
        reason: 'development_environment',
        currentVersion,
        storedVersion
      };
    }
    
    // If no stored version, this is first visit - just store current version
    if (!storedVersion) {
      setStoredVersion(currentVersion);
      return {
        cleared: false,
        reason: 'first_visit',
        currentVersion,
        storedVersion: null
      };
    }
    
    // Check if versions differ
    if (currentVersion !== storedVersion) {
      // Clear all browser data
      const clearingResults = await clearAllBrowserData();
      
      // Update stored version (this will recreate the localStorage entry)
      setStoredVersion(currentVersion);
      
      return {
        cleared: true,
        reason: 'version_update',
        currentVersion,
        storedVersion,
        clearingResults
      };
    }
    
    // Same version, no action needed
    return {
      cleared: false,
      reason: 'same_version',
      currentVersion,
      storedVersion
    };
    
  } catch (error) {
    console.error('❌ Error during version check:', error);
    
    // Try to store current version even if clearing failed
    try {
      setStoredVersion(getCurrentVersion());
    } catch (storeError) {
      console.error('❌ Could not store version after error:', storeError);
    }
    
    return {
      cleared: false,
      reason: 'error',
      error: error.message,
      currentVersion: getCurrentVersion(),
      storedVersion: getStoredVersion()
    };
  }
};

export const getVersionInfo = () => {
  const currentVersion = getCurrentVersion();
  const storedVersion = getStoredVersion();
  let lastClear = null;
  
  try {
    const lastClearStr = localStorage.getItem(LAST_CLEAR_KEY);
    if (lastClearStr) {
      lastClear = new Date(lastClearStr);
    }
  } catch (error) {
    console.warn('Could not get last clear date:', error);
  }
  
  return {
    currentVersion,
    storedVersion,
    lastClear,
    isNewVersion: isNewVersion(),
    isDevelopment: currentVersion === 'dev' || currentVersion === 'unknown'
  };
};

export const forceVersionClear = async () => {
  try {
    const clearingResults = await clearAllBrowserData();
    const currentVersion = getCurrentVersion();
    
    // Reset version storage
    setStoredVersion(currentVersion);
    
    return {
      success: true,
      currentVersion,
      clearingResults
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};